import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { api, ApiError, type OrderDetail } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo from "@/pages/home/components/Logo";
import DocumentButtons from "./components/DocumentButtons";
import StatusBadge from "./components/StatusBadge";
import { formatDate, formatMoney } from "./format";
import Footer from "@/pages/home/components/Footer";

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <dt className="text-sm text-foreground-500 shrink-0">{label}</dt>
      <dd className="text-sm font-medium text-foreground-950 text-right break-all">{children}</dd>
    </div>
  );
}

/** One credit purchase: what was bought, how it was paid, and its documents. */
export default function OrderDetailPage() {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);

  useEffect(() => {
    if (ready && !user) navigate("/auth", { replace: true });
  }, [ready, user, navigate]);

  useEffect(() => {
    if (!user || !orderId) return;
    let cancelled = false;
    setError(null);
    api
      .get<{ order: OrderDetail }>(`/orders/${encodeURIComponent(orderId)}`)
      .then(({ order }) => {
        if (!cancelled) setOrder(order);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError && (err.status === 404 || err.status === 400)
            ? t("orders.detail.notFound")
            : err instanceof Error
              ? err.message
              : t("common.errors.generic")
        );
      });
    return () => {
      cancelled = true;
    };
  }, [user, orderId, t]);

  const address = order?.billing.address;
  const addressLines = address
    ? [address.line1, address.line2, [address.postal_code, address.city].filter(Boolean).join(" "), address.country]
        .map((line) => line?.trim())
        .filter(Boolean)
    : [];

  return (
    <div className="min-h-screen bg-background-50">
      <header className="border-b border-background-200/70 bg-background-50/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto h-16 px-4 md:px-6 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <Link
              to="/profile"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors"
            >
              <i className="ri-arrow-left-line mr-1"></i>
              {t("orders.detail.backToProfile")}
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
        {error && (
          <div className="text-center max-w-sm mx-auto">
            <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-file-search-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-700">{error}</p>
            <Link
              to="/profile"
              className="mt-6 inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
            >
              {t("orders.detail.backToProfile")}
            </Link>
          </div>
        )}

        {!error && !order && (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 rounded-full loader-ring"></div>
          </div>
        )}

        {order && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-sm text-foreground-500">{t("orders.orderNumber", { id: order.id })}</p>
                <h1 className="mt-1 font-heading font-bold text-2xl text-foreground-950">
                  {t("orders.planLabel", { plan: order.planName })}
                </h1>
                <div className="mt-2">
                  <StatusBadge status={order.status} />
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-foreground-500">{t("orders.detail.total")}</p>
                <p className="mt-1 font-heading font-bold text-3xl text-foreground-950">
                  {formatMoney(order.amount, order.currency)}
                </p>
              </div>
            </div>

            {order.status !== "paid" && (
              <div
                className={`rounded-md px-4 py-3 text-sm ${
                  order.status === "cancelled" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"
                }`}
              >
                {t(`orders.detail.statusNote.${order.status}`)}
              </div>
            )}

            {order.status === "paid" && (
              <section className="bg-background-100 border border-background-200/70 rounded-lg p-6">
                <h2 className="font-heading font-bold text-base text-foreground-950">{t("orders.documents.title")}</h2>
                <p className="mt-1 text-sm text-foreground-500">
                  {order.receiptNumber
                    ? t("orders.documents.subtitle", { number: order.receiptNumber })
                    : t("orders.documents.subtitlePending")}
                </p>
                <div className="mt-4">
                  <DocumentButtons orderId={order.id} onError={setDocError} />
                </div>
                {docError && (
                  <div className="mt-3 rounded-md bg-red-100 text-red-700 px-4 py-3 text-sm">{docError}</div>
                )}
              </section>
            )}

            <section className="bg-background-100 border border-background-200/70 rounded-lg p-6">
              <h2 className="font-heading font-bold text-base text-foreground-950">{t("orders.detail.summary")}</h2>
              <div className="mt-4 rounded-md border border-background-300/60 bg-background-50">
                <div className="flex items-start justify-between gap-4 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-950">
                      {order.description ?? t("orders.planLabel", { plan: order.planName })}
                    </p>
                    <p className="mt-0.5 text-xs text-foreground-500">
                      {[
                        order.credits !== null ? t("orders.creditsCount", { count: order.credits }) : null,
                        order.level && order.level !== order.planName
                          ? t("orders.detail.benefitsLevel", { level: order.level })
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <div className="text-right text-sm shrink-0">
                    <p className="text-foreground-500">
                      {order.quantity} × {formatMoney(order.unitPrice, order.currency)}
                    </p>
                    {order.discount && (
                      <p className="text-primary-700">
                        {t("orders.detail.promo", { code: order.discount.code })} −
                        {formatMoney(order.discount.amount, order.currency)}
                      </p>
                    )}
                    <p className="font-semibold text-foreground-950">{formatMoney(order.amount, order.currency)}</p>
                  </div>
                </div>
              </div>
            </section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <section className="bg-background-100 border border-background-200/70 rounded-lg p-6">
                <h2 className="font-heading font-bold text-base text-foreground-950">{t("orders.detail.transaction")}</h2>
                <dl className="mt-2 divide-y divide-background-200/70">
                  <Row label={t("orders.detail.placed")}>{formatDate(order.createdAt, true)}</Row>
                  {order.paidAt && <Row label={t("orders.detail.paid")}>{formatDate(order.paidAt, true)}</Row>}
                  <Row label={t("orders.detail.method")}>
                    {order.provider
                      ? t("orders.detail.cardVia", { bank: t(`common.banks.${order.provider}`) })
                      : t("orders.detail.notChosen")}
                  </Row>
                  {order.reference && <Row label={t("orders.detail.reference")}>{order.reference}</Row>}
                  {order.receiptNumber && (
                    <Row label={t("orders.detail.receiptNumber")}>{order.receiptNumber}</Row>
                  )}
                  <Row label={t("orders.detail.currency")}>{order.currency}</Row>
                </dl>
              </section>

              <section className="bg-background-100 border border-background-200/70 rounded-lg p-6">
                <h2 className="font-heading font-bold text-base text-foreground-950">{t("orders.detail.billedTo")}</h2>
                <div className="mt-3 space-y-0.5 text-sm text-foreground-800">
                  {order.billing.name && <p className="font-semibold text-foreground-950">{order.billing.name}</p>}
                  {order.billing.company && <p>{order.billing.company}</p>}
                  {order.billing.email && <p className="break-all">{order.billing.email}</p>}
                  {order.billing.phone && <p>{order.billing.phone}</p>}
                  {addressLines.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                  {!order.billing.name && !order.billing.email && (
                    <p className="text-foreground-500">{t("orders.detail.noBilling")}</p>
                  )}
                </div>
              </section>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
